export interface ViewportSize {
  width: number;
  height: number;
}

export interface TrackBounds {
  width: number;
  height: number;
}

export interface CameraConfig {
  minZoom: number;
  maxZoom: number;
  lowSpeedBaseZoom: number;
  maxVisibleWorldWidth: number;
  maxVisibleWorldHeight: number;
  speedStartRatio: number;
  speedFullRatio: number;
  highSpeedZoomMultiplier: number;
  collisionImpulseThreshold: number;
  collisionZoomMultiplier: number;
  collisionHoldMs: number;
  collisionRecoveryRate: number;
  speedSmoothingRate: number;
  zoomInDampingRate: number;
  zoomOutDampingRate: number;
  speedDeadBand: number;
}

/**
 * Presentation limits are deliberately separate from physics constants. A
 * camera can reveal less of the track, but it must never change a simulation
 * tick or the state hash produced by that simulation.
 */
export const CAMERA_CONFIG: Readonly<CameraConfig> = Object.freeze({
  minZoom: 0.55,
  maxZoom: 4,
  lowSpeedBaseZoom: 1,
  maxVisibleWorldWidth: 1280,
  maxVisibleWorldHeight: 720,
  speedStartRatio: 0.2,
  speedFullRatio: 0.86,
  highSpeedZoomMultiplier: 0.84,
  collisionImpulseThreshold: 0.08,
  collisionZoomMultiplier: 1.16,
  collisionHoldMs: 180,
  collisionRecoveryRate: 5.5,
  speedSmoothingRate: 9,
  zoomInDampingRate: 10,
  zoomOutDampingRate: 4.5,
  speedDeadBand: 0.018,
});

export const DEFAULT_CAMERA_CONFIG = CAMERA_CONFIG;

export function clamp(value: number, minimum: number, maximum: number): number {
  return Math.min(maximum, Math.max(minimum, value));
}

/**
 * Computes the fair base zoom for a viewport. The visible world is capped in
 * both dimensions so ultrawide displays cannot gain a tactical advantage.
 */
export function viewportBaseZoom(
  viewport: ViewportSize,
  config: CameraConfig = CAMERA_CONFIG,
): number {
  const width = finitePositive(viewport.width, 640);
  const height = finitePositive(viewport.height, 480);
  const requiredWidthZoom = width / config.maxVisibleWorldWidth;
  const requiredHeightZoom = height / config.maxVisibleWorldHeight;
  const fairnessMinimum = Math.max(requiredWidthZoom, requiredHeightZoom);
  const highSpeedMultiplier = Math.max(config.highSpeedZoomMultiplier, 0.001);
  return clamp(
    Math.max(config.lowSpeedBaseZoom, fairnessMinimum / highSpeedMultiplier),
    config.minZoom,
    config.maxZoom,
  );
}

export const getViewportBaseZoom = viewportBaseZoom;

export function fairnessMinimumZoom(
  viewport: ViewportSize,
  config: CameraConfig = CAMERA_CONFIG,
): number {
  const width = finitePositive(viewport.width, 640);
  const height = finitePositive(viewport.height, 480);
  return Math.max(
    config.minZoom,
    width / config.maxVisibleWorldWidth,
    height / config.maxVisibleWorldHeight,
  );
}

export const getFairnessMinimumZoom = fairnessMinimumZoom;

/**
 * Returns the smallest zoom that keeps a track fully under the camera view.
 * The controller applies this value in addition to the fairness cap.
 */
export function minimumZoomForTrackBounds(
  viewport: ViewportSize,
  track: TrackBounds,
  config: CameraConfig = CAMERA_CONFIG,
): number {
  const width = finitePositive(viewport.width, 640);
  const height = finitePositive(viewport.height, 480);
  const trackWidth = finitePositive(track.width, width);
  const trackHeight = finitePositive(track.height, height);
  return Math.max(config.minZoom, width / trackWidth, height / trackHeight);
}

export const getMinimumZoomForTrackBounds = minimumZoomForTrackBounds;

function finitePositive(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}
